import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { FolderIdData, FolderNameData } from 'modules/domains/foldering';
import { renameWorkflowFolderMutation } from 'api/mutations/workflowIndex';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';

import {
  GraphQLRenameWorkflowFolderMutationResponse,
  GraphQLRenameWorkflowFolderMutationVariables,
} from '../types';

const displayText = {
  genericError: 'Could not rename folder',
};

export const useRenameFolder = () => {
  const dispatch = useDispatch();
  const [renameWorkflowFolder] = useMutation<
    GraphQLRenameWorkflowFolderMutationResponse,
    GraphQLRenameWorkflowFolderMutationVariables
  >(renameWorkflowFolderMutation);

  return useCallback(
    async (folder: FolderIdData & FolderNameData, newName: string) => {
      try {
        const { errors, data } = await renameWorkflowFolder({
          variables: {
            folderId: folder.id,
            newName,
          },
        });

        if (errors || !data?.renameWorkflowFolder) {
          return dispatch(
            addNegativeToastWithErrorId(displayText.genericError, 'renameWorkflowFolder'),
          );
        }

        if (data.renameWorkflowFolder.failure) {
          return dispatch(
            addNegativeToastWithErrorId(
              <ResponseWrapper>
                <NamedFolder name={folder.name} /> could not be renamed
                {data.renameWorkflowFolder.failure.name ? (
                  <span>
                    {' '}
                    to <NamedFolder name={data.renameWorkflowFolder.failure.name || ''} /> folder.
                  </span>
                ) : (
                  '.'
                )}
              </ResponseWrapper>,
              'renameWorkflowFolder',
            ),
          );
        }

        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <NamedFolder name={folder.name} /> was successfully renamed to{' '}
              <NamedFolder name={data.renameWorkflowFolder.success.folderName} /> folder.
            </ResponseWrapper>,
          ),
        );
      } catch (error) {
        return dispatch(
          addNegativeToastWithErrorId(displayText.genericError, 'renameWorkflowFolder'),
        );
      }
    },
    [renameWorkflowFolder],
  );
};
